/* =====================================================================
 * DM (Direct Message) Type Definitions
 * ===================================================================== */

/* -- 에이전트 타입 -- */
export type DMAgentType = 'system_app';

/* -- 메시지 방향 -- */
export type MessageDirection = 'agent_to_user' | 'user_to_agent';

/* -- 콘텐츠 타입 -- */
export type ContentType = 'text' | 'markdown' | 'card' | 'invitation' | 'file' | 'system';

/* -- 에이전트 상태 -- */
export type AgentStatus = 'online' | 'offline' | 'thinking';

/* -- WebSocket 이벤트 타입 (Server -> Client) -- */
export type DMEventType =
  | 'dm_message'
  | 'typing_start'
  | 'typing_stop'
  | 'read_receipt'
  | 'agent_status'
  /* 팀 DM */
  | 'user_message'
  | 'conversation_update'
  | 'user_dm_typing'
  | 'conversation_read';

/* -- WebSocket 이벤트 타입 (Client -> Server) -- */
export type DMClientEventType =
  | 'user_reply'
  | 'mark_read'
  | 'typing'
  /* 팀 DM */
  | 'send_user_message'
  | 'mark_conversation_read'
  | 'user_dm_typing';

/* -- 이모지 리액션 -- */
export type ReactionEmoji = string;

export interface Reaction {
  emoji: ReactionEmoji;
  /** 리액션을 누른 유저 ID 목록 */
  userIds: string[];
}

/* -- 초대 상태 -- */
export type InvitationStatus = 'pending' | 'accepted' | 'declined';

/* -- 메시지 메타데이터 -- */
export interface MessageMetadata {
  source?: string;
  provider?: string;
  event_type?: string;
  action_url?: string;
  card_data?: Record<string, unknown>;
  attachment_files?: Array<{
    file_id: string;
    name?: string;
    mime_type?: string;
    size?: number;
  }>;
  /* 팀 초대 */
  invitation_id?: string;
  invitation_status?: InvitationStatus;
  team_id?: string;
  team_name?: string;
  inviter_name?: string;
  /* 시스템 메시지 */
  system_event?: string;
  left_user_id?: string;
  left_user_name?: string;
  invited_user_id?: string;
  invited_user_name?: string;
  reason?: string;
}

/* -- DM 메시지 -- */
export interface DMMessage {
  id: string;
  user_id: string;
  agent_type: DMAgentType;
  direction: MessageDirection;
  content: string;
  content_type: ContentType;
  metadata?: MessageMetadata;
  created_at: string;
  delivered_at?: string | null;
  read_at?: string | null;
  reactions?: Reaction[];
}

/* -- WebSocket 이벤트 (Server -> Client) -- */
export interface DMEvent {
  type: DMEventType;
  data: Record<string, unknown>;
}

/* -- WebSocket 이벤트 (Client -> Server) -- */
export interface DMClientEvent {
  type: DMClientEventType;
  data: Record<string, unknown>;
}

/* -- 에이전트 정보 -- */
export interface DMAgentInfo {
  type: DMAgentType;
  name: string;
  description: string;
  avatar: string;
  /** 알파벳 아바타에 표시할 단일 문자 */
  letter: string;
  color: string;
}

/* -- 알림 설정 -- */
export interface NotificationSetting {
  enabled: boolean;
  schedule?: string;
  frequency?: string;
  providers?: string[];
}

export interface NotificationSettings {
  system_app: NotificationSetting;
}

/* -- DM 설정 -- */
export interface DMSettings {
  notification_settings: NotificationSettings;
  preferred_language: string;
  timezone: string;
}

/* -- API 응답 타입 -- */
export interface DMMessagesResponse {
  messages: DMMessage[];
  has_more: boolean;
}

export interface DMUnreadResponse {
  unread: Record<string, number>;
  total: number;
}

/* -- 앱 (시스템) 에이전트 정보 -- */
export const DM_APP_AGENT: DMAgentInfo = {
  type: 'system_app',
  name: 'askyour.ai',
  description: '시스템 알림 및 공지',
  avatar: '🤖',
  letter: 'A',
  color: '#10a37f',
};

export function getAgentInfo(type: DMAgentType): DMAgentInfo {
  return DM_APP_AGENT;
}

/* =====================================================================
 * 팀 DM (유저 간 대화) 타입
 * ===================================================================== */

export type ConversationType = 'direct' | 'group';

export interface Conversation {
  conversation_id: string;
  type: ConversationType;
  team_id: string;
  name: string | null;
  created_by: string;
  created_at: string;
  updated_at: string;
  last_message_at: string;
  last_message_preview: string | null;
  last_message_sender_id: string | null;
  unread_count: number;
  members?: ConversationMember[];
}

export interface ConversationMember {
  conversation_id: string;
  user_id: string;
  role: 'admin' | 'member';
  joined_at: string;
  last_read_at: string;
  unread_count: number;
  /** users 컬렉션에서 조인된 유저 정보 */
  name?: string | null;
  email?: string | null;
  picture?: string | null;
}

export interface UserMessage {
  message_id: string;
  conversation_id: string;
  sender_id: string;
  content: string;
  content_type: string;
  metadata: Record<string, unknown>;
  created_at: string;
  edited_at: string | null;
  reactions?: Reaction[];
}

/* =====================================================================
 * 팀 초대
 * ===================================================================== */

export interface TeamInvitation {
  invitation_id: string;
  team_id: string;
  inviter_id: string;
  invitee_id: string;
  invitee_email: string | null;
  invitee_name: string | null;
  inviter_name: string | null;
  conversation_id: string;
  message_id: string;
  status: InvitationStatus;
  created_at: string | null;
  responded_at: string | null;
}
